// Formatting built on Intl/toLocale* — each export keeps its legacy name and
// output shape. Timestamps are unix seconds.

const DATE_US = { month: "2-digit", day: "2-digit", year: "numeric" };
const TIME_12H = { hour: "2-digit", minute: "2-digit", hour12: true };

export const convertTimeStampToDate = ts =>
  ts
    ? new Date(ts * 1000).toLocaleDateString("en-US", DATE_US).replace(/\//g, ".")
    : undefined;

export const convertTimeStampToDateWithSlash = ts =>
  ts ? new Date(ts * 1000).toLocaleDateString("en-US", DATE_US) : undefined;

// "Wall-clock UTC" variant for picked times (booked-for, ETA): those times are
// stored as UTC literals, so the date must come from UTC too or the TZ offset
// can shift the displayed day (e.g. 23:00 UTC May 2 shown as May 3). Pairs
// with convertTimeStampToTime below.
export const convertWallClockTimeStampToDateWithSlash = ts =>
  ts
    ? new Date(ts * 1000).toLocaleDateString("en-US", { ...DATE_US, timeZone: "UTC" })
    : undefined;

export const daysLeftTillEndOfSubscription = endTimeStamp => {
  // Timestamp in seconds
  if (!endTimeStamp) return undefined;
  const diffTime = Math.abs(new Date(endTimeStamp * 1000) - new Date());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// UTC wall-clock time — see convertWallClockTimeStampToDateWithSlash.
export const convertTimeStampToTime = ts =>
  ts
    ? new Date(ts * 1000).toLocaleTimeString("en-US", { ...TIME_12H, timeZone: "UTC" })
    : undefined;

export const convertTimeStampToLocalTime = ts =>
  ts ? new Date(ts * 1000).toLocaleTimeString("en-US", TIME_12H) : undefined;

// Intl has month names but no ordinals ("3rd") — this stays hand-rolled.
const getOrdinalSuffix = day => {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const convertTimeStampToUsDateTime = ts => {
  if (!ts) return undefined;
  const date = new Date(ts * 1000);
  // UTC month/day to pair with convertTimeStampToTime (UTC hours) — a local
  // date next to a UTC time would look like the wrong day near midnight.
  const month = date.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
  const day = date.getUTCDate();
  return `${month} ${day}${getOrdinalSuffix(day)} at ${convertTimeStampToTime(ts)}`;
};

export const convertTimeStampToRequestFormat = ts =>
  ts
    ? new Date(ts * 1000).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23"
      })
    : undefined;

// en-CA locale renders YYYY-MM-DD
export const convertTimeStampToRequestDate = ts =>
  ts ? new Date(ts * 1000).toLocaleDateString("en-CA") : undefined;

// Bespoke copy ("5m ago") — Intl.RelativeTimeFormat can't produce it. Keep.
export const convertTimeStampToRelativeTime = timeStamp => {
  if (!timeStamp) return undefined;

  const seconds = Math.floor(Date.now() / 1000) - timeStamp;
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return convertTimeStampToDateWithSlash(timeStamp);
};

export const getUtcDateObject = ts => {
  if (!ts) return undefined;
  const date = new Date(ts * 1000);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getUTCHours(),
    date.getMinutes()
  );
};
